import { TranscriptionResult } from '@sos/shared';

export interface TranscriptionPanelOptions {
  onToggle: (active: boolean, targetLang: string) => void;
  onLanguageChange: (targetLang: string) => void;
}

export class LiveTranscriptionPanel {
  private element: HTMLElement;
  private subtitlesOverlay: HTMLElement;
  private entriesListElement: HTMLElement;
  private toggleBtn: HTMLButtonElement;
  private langSelect: HTMLSelectElement;
  private statusBadge: HTMLElement;
  private isActive: boolean = false;
  private entries: TranscriptionResult[] = [];

  constructor(
    private readonly videoContainer: HTMLElement,
    private readonly options: TranscriptionPanelOptions
  ) {
    // 1. Crear contenedor del panel de historial (para pestaña en herramientas)
    this.element = document.createElement('div');
    this.element.className = 'transcription-panel-container';
    this.element.style.display = 'flex';
    this.element.style.flexDirection = 'column';
    this.element.style.height = '100%';
    this.element.style.gap = '10px';

    // 2. Crear subtítulos flotantes sobre el video
    this.subtitlesOverlay = document.createElement('div');
    this.subtitlesOverlay.className = 'live-subtitles-overlay';
    this.subtitlesOverlay.style.display = 'none';
    this.videoContainer.appendChild(this.subtitlesOverlay);

    this.render();

    this.toggleBtn = this.element.querySelector('#btn-toggle-transcription') as HTMLButtonElement;
    this.langSelect = this.element.querySelector('#select-transcription-lang') as HTMLSelectElement;
    this.entriesListElement = this.element.querySelector('#transcription-entries-list') as HTMLElement;
    this.statusBadge = this.element.querySelector('#transcription-status-badge') as HTMLElement;

    this.setupListeners();
  }

  private render(): void {
    this.element.innerHTML = `
      <!-- Cabecera de Control del Motor IA -->
      <div class="transcription-controls-bar" style="display:flex; align-items:center; justify-content:space-between; gap:8px; padding-bottom:8px; border-bottom:1px solid var(--border-dim, rgba(255,255,255,0.1)); flex-wrap:wrap;">
        <div style="display:flex; align-items:center; gap:8px;">
          <button id="btn-toggle-transcription" class="btn-primary" style="background:#0284c7; border-color:#0284c7; padding:6px 12px; font-size:0.8rem; display:inline-flex; align-items:center; gap:6px;">
            <span class="stt-dot" style="width:8px; height:8px; border-radius:50%; background:#94a3b8; display:inline-block;"></span>
            <span>🎙️ Activar IA</span>
          </button>
          
          <select id="select-transcription-lang" class="form-select" style="background:rgba(255,255,255,0.06); color:#fff; border:1px solid var(--border-dim, rgba(255,255,255,0.2)); border-radius:8px; padding:4px 8px; font-size:0.75rem;">
            <option value="en">Traducir a Inglés (EN)</option>
            <option value="es" selected>Traducir a Español (ES)</option>
          </select>
        </div>

        <div id="transcription-status-badge" style="font-size:0.7rem; color:#38bdf8; font-family:var(--font-mono, monospace);">
          ● MOTOR: LISTO (STANDBY)
        </div>
      </div>

      <!-- Lista Histórica de Transcripciones -->
      <div id="transcription-entries-list" style="flex:1; overflow-y:auto; display:flex; flex-direction:column; gap:8px; padding:4px 0;">
        <div class="empty-transcription-hint" style="text-align:center; padding:32px 16px; color:#64748b; font-size:0.85rem;">
          Presione <strong>"Activar IA"</strong> durante una llamada activa para transcribir y traducir en vivo con Faster-Whisper.
        </div>
      </div>

      <!-- Barra de Acciones Rápidas -->
      <div style="display:flex; justify-content:space-between; align-items:center; padding-top:6px; border-top:1px solid var(--border-dim, rgba(255,255,255,0.1));">
        <span style="font-size:0.7rem; color:#64748b;">Inferencia local en OVHcloud (CPU INT8)</span>
        <button id="btn-copy-transcripts" class="btn-ghost" style="padding:4px 8px; font-size:0.75rem;" title="Copiar transcripción completa al portapapeles">
          📋 Copiar al Informe
        </button>
      </div>
    `;
  }

  private setupListeners(): void {
    this.toggleBtn.addEventListener('click', () => {
      this.isActive = !this.isActive;
      this.updateActiveStateUI();
      this.options.onToggle(this.isActive, this.langSelect.value);
    });

    this.langSelect.addEventListener('change', () => {
      this.options.onLanguageChange(this.langSelect.value);
    });

    this.element.querySelector('#btn-copy-transcripts')?.addEventListener('click', () => {
      this.copyTranscriptsToClipboard();
    });
  }

  public async checkHealth(healthUrl: string = '/transcribe/health'): Promise<void> {
    try {
      const res = await fetch(healthUrl, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'healthy' || data.status === 'ok') {
          if (!this.isActive) {
            this.statusBadge.innerHTML = `<span style="color:#10b981;" title="Modelo Whisper ${data.model} activo en CPU">● WHISPER ONLINE (${(data.model || 'CPU').toUpperCase()})</span>`;
          }
          return;
        }
      }
    } catch {
      // Ignorar fallback silencioso
    }
  }

  public setStatus(connected: boolean): void {
    if (connected) {
      this.statusBadge.innerHTML = '<span style="color:#10b981;">● TRANSCRIBIENDO EN VIVO</span>';
    } else {
      this.statusBadge.innerHTML = '<span style="color:#38bdf8;">● MOTOR: LISTO (STANDBY)</span>';
    }
  }

  private updateActiveStateUI(): void {
    const dot = this.toggleBtn.querySelector('.stt-dot') as HTMLElement;
    const label = this.toggleBtn.querySelector('span:last-child') as HTMLElement;

    if (this.isActive) {
      this.toggleBtn.style.background = '#10b981';
      this.toggleBtn.style.borderColor = '#10b981';
      if (dot) dot.style.background = '#fff';
      if (label) label.textContent = '⏹ Detener IA';
      this.subtitlesOverlay.style.display = 'block';
      this.statusBadge.innerHTML = '<span style="color:#10b981;">● ESCUCHANDO...</span>';
    } else {
      this.toggleBtn.style.background = '#0284c7';
      this.toggleBtn.style.borderColor = '#0284c7';
      if (dot) dot.style.background = '#94a3b8';
      if (label) label.textContent = '🎙️ Activar IA';
      this.subtitlesOverlay.style.display = 'none';
      this.statusBadge.innerHTML = '<span style="color:#94a3b8;">MOTOR: PAUSADO</span>';
    }
  }

  public addTranscription(result: TranscriptionResult): void {
    this.entries.push(result);

    // 1. Actualizar subtítulo flotante en el video
    this.renderSubtitlesOverlay(result);

    // 2. Limpiar placeholder si es el primer elemento
    const emptyHint = this.entriesListElement.querySelector('.empty-transcription-hint');
    if (emptyHint) emptyHint.remove();

    // 3. Crear tarjeta en el historial
    const card = document.createElement('div');
    card.className = 'transcription-card';
    card.style.background = 'rgba(255, 255, 255, 0.04)';
    card.style.border = '1px solid var(--border-dim, rgba(255, 255, 255, 0.1))';
    card.style.borderRadius = '10px';
    card.style.padding = '8px 12px';
    card.style.fontSize = '0.82rem';
    card.style.lineHeight = '1.4';

    const timeStr = new Date(result.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const hasTranslation = result.translatedText && result.translatedText !== result.originalText;

    card.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
        <div style="display:flex; align-items:center; gap:6px;">
          <span style="font-weight:700; color:#38bdf8; font-size:0.75rem;">CIUDADANO</span>
          <span style="background:rgba(56,189,248,0.15); color:#38bdf8; padding:1px 6px; border-radius:4px; font-size:0.68rem; font-weight:700; text-transform:uppercase;">
            ${result.detectedLanguage.toUpperCase()}
          </span>
        </div>
        <span style="font-size:0.7rem; color:#64748b; font-family:var(--font-mono, monospace);">${timeStr}</span>
      </div>

      <div style="color:#f8fafc; font-weight:500;">
        "${result.originalText}"
      </div>

      ${hasTranslation ? `
        <div style="margin-top:4px; padding-top:4px; border-top:1px dashed rgba(255,255,255,0.08); color:#a3e635; font-size:0.78rem;">
          <span style="color:#64748b; font-weight:700; font-size:0.68rem;">TRADUCCIÓN:</span> "${result.translatedText}"
        </div>
      ` : ''}
    `;

    this.entriesListElement.appendChild(card);
    this.entriesListElement.scrollTop = this.entriesListElement.scrollHeight;
  }

  private renderSubtitlesOverlay(result: TranscriptionResult): void {
    const hasTranslation = result.translatedText && result.translatedText !== result.originalText;

    this.subtitlesOverlay.innerHTML = `
      <div class="subtitles-box" style="
        position: absolute;
        bottom: 56px;
        left: 20px;
        right: 20px;
        background: rgba(8, 10, 15, 0.88);
        border: 1px solid rgba(56, 189, 248, 0.4);
        border-radius: 12px;
        padding: 10px 16px;
        color: #fff;
        font-family: var(--font-sans, system-ui);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(8px);
        animation: subtitleFadeIn 0.25s ease-out;
        z-index: 25;
      ">
        <div style="display:flex; align-items:center; gap:6px; margin-bottom:2px;">
          <span style="width:6px; height:6px; border-radius:50%; background:#10b981; display:inline-block;"></span>
          <span style="font-size:0.7rem; font-weight:700; color:#38bdf8; text-transform:uppercase;">AUDIO ESCENA [${result.detectedLanguage.toUpperCase()}]</span>
        </div>
        <div style="font-size:0.95rem; font-weight:600; line-height:1.3; color:#f8fafc;">
          ${result.originalText}
        </div>
        ${hasTranslation ? `
          <div style="font-size:0.85rem; font-weight:500; color:#a3e635; margin-top:3px; padding-top:3px; border-top:1px solid rgba(255,255,255,0.1);">
            🌐 ${result.translatedText}
          </div>
        ` : ''}
      </div>
    `;

    // Desvanecer subtítulo tras 7 segundos de inactividad
    clearTimeout((this.subtitlesOverlay as any)._timeout);
    (this.subtitlesOverlay as any)._timeout = setTimeout(() => {
      this.subtitlesOverlay.innerHTML = '';
    }, 7000);
  }

  private copyTranscriptsToClipboard(): void {
    if (this.entries.length === 0) {
      alert('Aún no hay frases transcritas en esta llamada.');
      return;
    }

    const report = this.entries.map((e) => {
      const time = new Date(e.timestamp).toLocaleTimeString();
      let text = `[${time}] [${e.detectedLanguage.toUpperCase()}] ${e.originalText}`;
      if (e.translatedText && e.translatedText !== e.originalText) {
        text += `\n       -> Traducción: ${e.translatedText}`;
      }
      return text;
    }).join('\n\n');

    navigator.clipboard.writeText(`=== HISTORIAL DE VOZ TELE-TRIAGE 911 ===\n\n${report}\n`)
      .then(() => alert('¡Transcripción y traducción copiada al portapapeles!'))
      .catch(() => alert('Error al copiar al portapapeles.'));
  }

  public mount(container: HTMLElement): void {
    container.innerHTML = '';
    container.appendChild(this.element);
  }

  public reset(): void {
    this.entries = [];
    this.isActive = false;
    this.updateActiveStateUI();
    this.subtitlesOverlay.innerHTML = '';
    this.render();
    this.toggleBtn = this.element.querySelector('#btn-toggle-transcription') as HTMLButtonElement;
    this.langSelect = this.element.querySelector('#select-transcription-lang') as HTMLSelectElement;
    this.entriesListElement = this.element.querySelector('#transcription-entries-list') as HTMLElement;
    this.statusBadge = this.element.querySelector('#transcription-status-badge') as HTMLElement;
    this.setupListeners();
  }
}
